import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import {
  Box,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
} from "@mui/material";
import Button from "@mui/material/Button";
import { styled, Theme } from "@mui/material/styles";
import { useCallback, useEffect, useState } from "react";
import { Accept } from "react-dropzone";
import { useBoolean } from "usehooks-ts";

import LoadingSpinner from "@/common/LoadingSpinner";

import { DialogHeader } from "../DialogHeader/DialogHeader";
import { FilePicker, FilepickerFile } from "../FilePicker";
import { Icon } from "../Icon";
import { MAX_COVER_FILES } from "./constants";
import { SortableObjectTile } from "./SortableObjectTile";
import { CoverFile } from "./types";

const ACCEPTED_FILE_TYPES: Accept = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
};

interface EditDialogProps {
  open: boolean;
  onClose: () => void;
  files: CoverFile[];
  isBusy?: boolean;
  onFilesChanged: (fileIds: string[]) => void;
}

const Container = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(1),
  width: "100%",
  maxWidth: "800px",
  margin: "0 auto",
  pointerEvents: "auto",
  overflow: "hidden",
}));

const ImageContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  border: `1px solid ${theme.palette.secondary[6]}`,
  aspectRatio: 1,

  "&:first-of-type": {
    border: `2px solid ${theme.palette.primary.main}`,

    "&:after": {
      display: "inline-flex",
      alignItems: "center",
      content: "'Primary'",
      position: "absolute",
      height: 25,
      top: 0,
      left: 0,
      padding: theme.spacing(1, 2),
      backgroundColor: theme.palette.primary.main,
      borderRadius: theme.spacing(0, 0, 1, 0),
    },
  },
}));

const AddFileButton = styled(ButtonBase)(({ theme }) => ({
  aspectRatio: "1/1",
  width: "100%",
  gap: theme.spacing(2),
  borderRadius: "8px",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.secondary["7/75"],
  backdropFilter: "blur(2px)",
  border: `dashed 1px ${theme.palette.secondary["3/50"]}`,
  "&:hover": {
    border: `dashed 1px ${theme.palette.secondary["3/75"]}`,
  },
}));

const PlusIconContainer = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: "50%",
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.secondary[4]}`,
  backgroundColor: theme.palette.secondary[5],

  "&:hover": {
    borderColor: theme.palette.secondary[3],
    backgroundColor: theme.palette.secondary[4],
  },
}));

export const EditDialog = (props: EditDialogProps) => {
  const { open, onClose, isBusy, files, onFilesChanged } = props;

  const {
    value: isFilePickerOpen,
    setTrue: openFilePicker,
    setFalse: closeFilePicker,
  } = useBoolean();

  // Local state for optimistic updates. Otherwise, the drag and drop experience is funky.
  const [cachedFiles, setCachedFiles] = useState<CoverFile[]>(files);

  // We update the cached files when the files prop changes
  useEffect(() => {
    if (isBusy) return;

    setCachedFiles(files);
  }, [isBusy, files, setCachedFiles]);

  const placeholderCount = MAX_COVER_FILES - cachedFiles.length;

  const handleDialogClose = () => {
    if (isBusy) return;
    onClose();
  };

  const handleFilesSelect = useCallback(
    (newFiles: FilepickerFile[]) => {
      const nextFileIds = [
        ...cachedFiles,
        ...newFiles.map<CoverFile>((f) => ({
          id: f.id,
          name: f.name,
          previewUrl: f.thumbnailSrc,
        })),
      ];

      setCachedFiles(nextFileIds);
      onFilesChanged(nextFileIds.map((file) => file.id));
    },
    [cachedFiles, setCachedFiles, onFilesChanged],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over) return;

      if (active.id === over.id) return;

      const oldIx = cachedFiles.findIndex((file) => file.id === active.id);
      const newIx = cachedFiles.findIndex((file) => file.id === over.id);

      const newFiles = arrayMove(cachedFiles, oldIx, newIx);
      setCachedFiles(newFiles);

      onFilesChanged(newFiles.map((file) => file.id));
    },
    [cachedFiles, setCachedFiles, onFilesChanged],
  );

  const handleDelete = (deletedFileId: string) => {
    const newFiles = cachedFiles.filter((file) => file.id !== deletedFileId);

    setCachedFiles(newFiles);
    onFilesChanged(newFiles.map((file) => file.id));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose}>
      <DialogHeader title="Add, reorder or delete cover images" />
      <Divider />
      <DialogContent>
        <DndContext onDragEnd={handleDragEnd}>
          <SortableContext items={cachedFiles.map((file) => file.id)}>
            <Container>
              {cachedFiles.map((file) => (
                <ImageContainer key={file.id}>
                  <SortableObjectTile
                    file={file}
                    onDelete={() => handleDelete(file.id)}
                  />
                </ImageContainer>
              ))}
              {Array.from({ length: placeholderCount }).map((_, index) => (
                <AddFileButton
                  key={`placeholder-${index}`}
                  onClick={openFilePicker}
                >
                  <PlusIconContainer>
                    <Icon
                      icon={faPlus}
                      style={{ minWidth: 14, minHeight: 14 }}
                    />
                  </PlusIconContainer>
                </AddFileButton>
              ))}
            </Container>
          </SortableContext>
        </DndContext>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          variant="contained"
          disabled={isBusy}
          color="secondary"
          onClick={onClose}
        >
          <Stack
            direction="row"
            sx={{
              gap: 2,
              alignItems: "center",
            }}
          >
            {isBusy && <LoadingSpinner />}
            Close
          </Stack>
        </Button>
      </DialogActions>
      <FilePicker
        open={isFilePickerOpen}
        onClose={closeFilePicker}
        onFilesSelect={handleFilesSelect}
        categories={["recent", "drive"]}
        accept={ACCEPTED_FILE_TYPES}
        maxFiles={placeholderCount}
        modality="IMAGE"
      />
    </Dialog>
  );
};
